import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

// Problem tags — positioned as if orbiting the four figures' heads.
// Each pair of coordinates is a percentage of the container box.
// Figure positions (approximate x centers on the 3:2 image): 20%, 38%, 52%, 68%.
const problems = [
  { text: "Housing",        x: "16%", y: "20%", dur: 6.0, delay: 0.0 },
  { text: "Healthcare",     x: "31%", y: "14%", dur: 7.2, delay: 0.4 },
  { text: "VA Benefits",    x: "45%", y: "19%", dur: 6.6, delay: 0.9 },
  { text: "Employment",     x: "60%", y: "13%", dur: 7.8, delay: 1.3 },
  { text: "Mental Health",  x: "74%", y: "17%", dur: 6.4, delay: 0.6 },
  { text: "Family Support", x: "22%", y: "24%", dur: 8.0, delay: 1.6, hideSm: true },
  { text: "Education",      x: "39%", y: "22%", dur: 6.8, delay: 2.0, hideSm: true },
  { text: "Transportation", x: "56%", y: "24%", dur: 7.4, delay: 0.2 },
  { text: "Legal Aid",      x: "70%", y: "22%", dur: 7.0, delay: 1.1, hideSm: true },
  { text: "Food Security",  x: "12%", y: "22%", dur: 6.2, delay: 2.4, hideSm: true },
  { text: "Childcare",      x: "82%", y: "22%", dur: 7.6, delay: 1.8 },
  { text: "Entertainment",  x: "90%", y: "13%", dur: 6.9, delay: 0.7 },
  { text: "Start Business", x: "52%", y: "8%",  dur: 7.1, delay: 2.8 },
];

/**
 * Full-bleed hero banner showing four figures overlooking Pittsburgh at sunset,
 * with fragmented-support "problems" swirling above their heads until WHO
 * organizes them. Uses framer-motion for the orbital float animation.
 */
export default function PittsburghHero() {
  return (
    <section
      className="relative w-full overflow-hidden bg-navy-deep"
      data-testid="pittsburgh-hero"
    >
      {/* Background image — text NEVER overlays the figures:
          desktop overlays copy in the sky only; smaller screens get a plain image
          with the copy in a navy band beneath it. */}
      <div className="relative w-full aspect-[16/10] md:aspect-[16/9] lg:aspect-[16/8] max-h-[780px]">
        <img
          src="/assets/pittsburgh-hero-v2.jpg"
          alt="Five figures — four veterans and civilians and a child holding hands — standing on Mt. Washington overlooking the Pittsburgh skyline at sunset"
          className="absolute inset-0 w-full h-full object-cover brightness-110"
          data-testid="pittsburgh-hero-image"
          fetchPriority="high"
        />

        {/* Vignettes: darken top for headline legibility (desktop), keep figures clear */}
        <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/60 via-transparent to-navy-deep/30 pointer-events-none" aria-hidden />

        {/* Swirling problem/service chips — all screen sizes, confined to the sky */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          {problems.map((p, i) => (
            <ProblemChip key={p.text} {...p} index={i} />
          ))}
        </div>

        {/* Headline + CTAs at the bottom of the image — desktop only; on mobile/tablet the
            image stays pure and all copy renders in the band below */}
        <div className="absolute inset-x-0 bottom-0 hidden lg:block pointer-events-none">
          <div className="bg-gradient-to-t from-navy-deep/85 via-navy-deep/40 to-transparent px-6 lg:px-10 pt-14 pb-6">
            <div className="max-w-[1240px] mx-auto flex flex-wrap items-end justify-between gap-6">
              <p className="font-display font-black text-cream text-2xl lg:text-4xl leading-tight max-w-2xl [text-shadow:0_2px_14px_rgba(10,25,47,0.9),0_0_4px_rgba(10,25,47,0.9)]" data-testid="pittsburgh-hero-caption">
                Strengthening the Connections That Support Veterans.
              </p>
              <div className="flex flex-wrap gap-4 pointer-events-auto" data-testid="pittsburgh-hero-ctas">
                <Link
                  to="/solution"
                  data-testid="pittsburgh-hero-solution-btn"
                  className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-6 py-3.5 rounded-md transition-colors"
                >
                  See how WHO helps <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                </Link>
                <Link
                  to="/partners"
                  data-testid="pittsburgh-hero-partners-btn"
                  className="inline-flex items-center gap-2 border border-cream/30 hover:border-gold hover:text-gold text-cream font-semibold px-6 py-3.5 rounded-md transition-colors"
                >
                  Join the Partner Network
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="hidden lg:block px-6 lg:px-10 py-8 border-t border-white/5" data-testid="pittsburgh-hero-subcopy-strip">
        <p className="max-w-[1240px] mx-auto text-cream/85 text-lg leading-relaxed" data-testid="pittsburgh-hero-subcopy">
          Veterans and their families deserve meaningful connections to the services and opportunities
          available to them. Every day, dedicated service providers work to meet those needs, often across
          complex systems and with limited resources. WHO is being designed to strengthen the connections
          between veterans and the organizations committed to serving them&mdash;improving coordination,
          supporting follow-through, and helping ensure that meaningful connections do not end with a referral.
        </p>
      </div>

      {/* Full copy below the image on phones/tablets — people never covered;
          headline leads this dedicated section immediately below the image */}
      <div className="lg:hidden px-6 py-10 md:px-10" data-testid="pittsburgh-hero-copy-below">
        <HeroCopy />
      </div>
    </section>
  );
}

function HeroCopy({ compact = false, noSubcopy = false, noHeadline = false }) {
  return (
    <div className={compact ? "" : "max-w-2xl"}>
      {!noHeadline && (
        <motion.h2
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="font-display font-black text-cream tracking-tight leading-[0.98] text-3xl sm:text-4xl md:text-5xl lg:text-[3.4rem]"
          data-testid="pittsburgh-hero-headline"
        >
          Strengthening the Connections That Support Veterans.
        </motion.h2>
      )}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.45, duration: 0.6 }}
        className="mt-6 flex flex-wrap gap-4"
      >
        <Link
          to="/solution"
          data-testid="pittsburgh-hero-solution-btn"
          className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-6 py-3.5 rounded-md transition-colors"
        >
          See how WHO helps <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
        </Link>
        <Link
          to="/partners"
          data-testid="pittsburgh-hero-partners-btn"
          className="inline-flex items-center gap-2 border border-cream/30 hover:border-gold hover:text-gold text-cream font-semibold px-6 py-3.5 rounded-md transition-colors"
        >
          Join the Partner Network
        </Link>
      </motion.div>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.7 }}
        className={`mt-6 text-cream/85 text-base md:text-lg leading-relaxed max-w-2xl ${noSubcopy ? "hidden" : ""}`}
        data-testid="pittsburgh-hero-subcopy-mobile"
      >
        Veterans and their families deserve meaningful connections to the services and opportunities
        available to them. Every day, dedicated service providers work to meet those needs, often across
        complex systems and with limited resources.
        <span className="text-gold-light"> WHO is being designed to strengthen the connections between veterans and the organizations committed to serving them.</span>
      </motion.p>
    </div>
  );
}

function ProblemChip({ text, x, y, dur, delay, index, hideSm }) {
  // Orbital float: each chip drifts in a soft loop while gently fading in/out.
  const radiusX = 12 + (index % 3) * 4;
  const radiusY = 8 + (index % 4) * 3;
  return (
    <motion.div
      style={{ left: x, top: y }}
      className={`absolute -translate-x-1/2 -translate-y-1/2 ${hideSm ? "hidden md:block" : ""}`}
      initial={{ opacity: 0, scale: 0.7 }}
      animate={{
        opacity: [0, 0.9, 0.9, 0],
        scale: [0.7, 1, 1, 0.7],
        x: [0, radiusX, 0, -radiusX, 0],
        y: [0, -radiusY, 0, radiusY, 0],
      }}
      transition={{
        duration: dur,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      <span className="inline-flex items-center px-2.5 md:px-3.5 py-1 md:py-1.5 rounded-full bg-navy-deep/70 backdrop-blur-md border border-gold/40 text-cream text-[10px] md:text-sm font-medium tracking-tight shadow-lg">
        {text}<span className="text-gold ml-0.5">?</span>
      </span>
    </motion.div>
  );
}
